// Dual-Space Coach hook
// SSE streaming for AI coaching in the problem-solution map

import { useState, useCallback, useRef } from 'react'
import { dualSpaceApi } from '@/lib/api/client'

export interface CoachMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  timestamp: Date
  suggestedLinks?: SuggestedLink[]
  gaps?: CoachGap[]
  quickActions?: { label: string; type: string; payload?: string }[]
}

export interface SuggestedLink {
  sourceType: string
  sourceId: string
  sourceTitle: string
  targetType: string
  targetId: string
  targetTitle: string
  relation: string
  rationale: string
}

export interface CoachGap {
  type: string
  entityId: string
  entityTitle: string
  message: string
}

export function useDualSpaceCoach(accountId: string, projectId: string) {
  const [messages, setMessages] = useState<CoachMessage[]>([])
  const [isStreaming, setIsStreaming] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const abortRef = useRef<AbortController | null>(null)

  const sendMessage = useCallback(async (
    content: string,
    apiKey: string,
    selectedNode?: { id: string; type: string; title: string },
  ) => {
    const userMsg: CoachMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content,
      timestamp: new Date(),
    }
    setMessages(prev => [...prev, userMsg])
    setIsStreaming(true)
    setError(null)

    const assistantId = `assistant-${Date.now()}`
    let fullContent = ''

    setMessages(prev => [...prev, {
      id: assistantId,
      role: 'assistant',
      content: '',
      timestamp: new Date(),
    }])

    try {
      abortRef.current = new AbortController()
      const response = await dualSpaceApi.coachStream(accountId, projectId, {
        message: content,
        apiKey,
        selectedNodeId: selectedNode?.id,
        selectedNodeType: selectedNode?.type,
        selectedNodeTitle: selectedNode?.title,
      })

      if (!response.ok) {
        throw new Error(`Coach API error: ${response.status}`)
      }

      const reader = response.body?.getReader()
      if (!reader) throw new Error('No response body')

      const decoder = new TextDecoder()
      let buffer = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const event = JSON.parse(line.slice(6))
              if (event.type === 'text' && event.content) {
                fullContent += event.content
                setMessages(prev => prev.map(m =>
                  m.id === assistantId ? { ...m, content: fullContent } : m,
                ))
              } else if (event.type === 'quick_actions' && event.actions) {
                setMessages(prev => prev.map(m =>
                  m.id === assistantId ? { ...m, quickActions: event.actions } : m,
                ))
              } else if (event.type === 'done') {
                // Parse metadata from the full content for suggested links / gaps
                const meta = parseMetaBlock(fullContent)
                if (meta) {
                  setMessages(prev => prev.map(m =>
                    m.id === assistantId ? {
                      ...m,
                      content: meta.cleanContent,
                      suggestedLinks: meta.suggestedLinks,
                      gaps: meta.gaps,
                    } : m,
                  ))
                }
              }
            } catch {
              // Skip unparseable lines
            }
          }
        }
      }
    } catch (err: any) {
      if (err.name !== 'AbortError') {
        setError(err?.message || 'Coach failed')
        setMessages(prev => prev.filter(m => m.id !== assistantId))
      }
    } finally {
      setIsStreaming(false)
      abortRef.current = null
    }
  }, [accountId, projectId])

  const abort = useCallback(() => {
    abortRef.current?.abort()
  }, [])

  return { messages, isStreaming, error, sendMessage, abort }
}

function parseMetaBlock(content: string): {
  cleanContent: string
  suggestedLinks?: SuggestedLink[]
  gaps?: CoachGap[]
} | null {
  const match = content.match(/```json:meta\s*([\s\S]*?)```/)
  if (!match) return null

  const cleanContent = content.replace(/```json:meta\s*[\s\S]*?```/, '').trim()
  try {
    const meta = JSON.parse(match[1])
    return {
      cleanContent,
      suggestedLinks: meta.suggestedLinks,
      gaps: meta.gaps,
    }
  } catch {
    return { cleanContent }
  }
}
